import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useNavigate } from "react-router-dom";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

export const AdminAthleteMessageManage = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(1);

  // ✅ Fetch all grouped athlete messages
  useEffect(() => {
    const fetchMessages = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${API_URL.ADMIN_MANAGE_ATHLETE_MESSAGES}?page=${page}&limit=${limit}&search=${search}`,
          {
            headers: { Authorization: `Bearer ${staff.token}` },
          },
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Failed to fetch messages");
        }

        console.log("Grouped messages:", data.data);
        setMessages(data.data);
        setTotalPages(data.totalPages);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [page, search, staff.token]);

  // ✅ Navigate to reply screen using athlete_id
  const handleReply = (athleteId) => {
    navigate(`/administrator/athlete/message/${athleteId}`);
  };

  return (
    <div className="md:flex md:h-screen">
      <StaffSideMenu />
      <div className="max-md:w-full md:flex-1 bg-bgBase flex flex-col overflow-auto">
        <div className="bg-white px-5 md:px-16 py-6">
          <h1 className="font-inter font-medium text-dark text-2xl !leading-none max-md:pl-12 mb-6">
            Manage Athlete Messages
          </h1>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 text-red-500 p-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Search Bar */}
          <div className="flex items-center">
            <input
              type="text"
              placeholder="Search message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-md:min-w-56 lg:w-1/4 p-3 border border-black/10 rounded-lg font-inter font-normal text-sm"
            />
          </div>
        </div>

        <div className="px-5 md:px-16 py-6">
          {/* Message Table */}
          {isLoading ? (
            <div className="bg-white border border-tblBorder rounded-md w-fit mx-auto py-2 px-5">
              <p className="text-dark text-sm font-inter font-normal text-center">Loading...</p>
            </div>
          ) : (
            <div className="overflow-auto show-scrollbar max-h-[680px]">
              <div className="rounded-10 border border-tblBorder w-full">
                <table className="table-auto table-radius w-full min-w-[768px]">
                  <thead className="sticky top-0 bg-tblHead border-b border-tblBorder text-dark font-inter !font-normal text-sm">
                    <tr>
                      <th className="px-6 py-4 text-left">Athlete Name</th>
                      <th className="px-6 py-4 text-left">Latest Message</th>
                      <th className="px-6 py-4 whitespace-nowrap text-left">Total Messages</th>
                      <th className="px-6 py-4 text-left">Last Updated</th>
                      <th className="px-6 py-4 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          No messages found.
                        </td>
                      </tr>
                    ) : (
                      messages.map((msg, index) => (
                        <tr
                          key={msg.athlete_id}
                          className={` text-dark font-inter font-normal text-sm ${index % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}`}
                        >
                          <td className="px-6 py-4">{msg.athleteName}</td>
                          <td className="px-6 py-4">{msg.latestMessage}</td>
                          <td className="px-6 py-4">{msg.totalMessages}</td>
                          <td className="px-6 py-4">
                            {new Date(msg.latestTimestamp).toLocaleString()}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleReply(msg.athlete_id)}
                              className="bg-dark hover:bg-black rounded-10 py-2 px-4 text-white text-sm font-inter font-normal"
                            >
                              Reply
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {/* Pagination */}
        <div className="bg-white px-5 md:px-16 py-6 mt-5 md:mt-auto">
          <div className="flex max-sm:flex-col items-center justify-between">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm `}
            >
              <MdKeyboardArrowLeft size={20} /> Previous
            </button>

            <p className="text-dark font-inter font-normal text-sm ">
              Page {page} of {totalPages}
            </p>
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
              className="px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm "
            >
              Next <MdKeyboardArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
